/*eslint-disable*/
import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@mui/styles";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Info from "@mui/icons-material/Info";
import AccountBox from "@mui/icons-material/AccountBox";
import House from "@mui/icons-material/House";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import Button from "components/CustomButtons/Button.js";
import styles from "../../styles/headerLinksStyle.js";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme, useMediaQuery, Box, Typography } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import { FONT_FAMILY } from "common/sharedFunctions.js";
import { navigateLocalized, toLocalizedPath } from "../../utils/i18nRoute"; // [I18N_ROUTE]
import { navigateWithIframeEscape } from "../../utils/navigationUtils";
import moment from "moment";
import { haptic } from "../../utils/haptics";
import { useThemeMode } from "../../contexts/ThemeModeContext";

const useStyles = makeStyles(styles);

export default function HeaderLinks(props) {
  const classes = useStyles();
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const languagedata = useSelector((state) => state.languagedata);
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [loggedIn, setLoggedIn] = useState(false);
  const navigate = useNavigate();

  const [scrolled, setScrolled] = useState(false);

  const localizedNavigate = (path) => {
    haptic.light();
    navigateLocalized(navigate, path, window.location.pathname);
  };

  const currentActiveLang = (i18n.language || "fr").startsWith("en") ? "en" : "fr";

  useEffect(() => {
    if (typeof window === "undefined" || props.scrolled !== undefined) return;
    const update = () => {
      const se = document.scrollingElement || document.documentElement;
      const y = Math.max(0, window.scrollY || window.pageYOffset || se?.scrollTop || document.body?.scrollTop || 0);
      setScrolled(y > 10);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("scrollend", update, { passive: true });
    const se = document.scrollingElement || document.documentElement;
    if (se) {
      se.addEventListener("scroll", update, { passive: true });
      se.addEventListener("scrollend", update, { passive: true });
    }
    window.addEventListener("wheel", update, { passive: true });
    window.addEventListener("touchmove", update, { passive: true });
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("scrollend", update);
      if (se) {
        se.removeEventListener("scroll", update);
        se.removeEventListener("scrollend", update);
      }
      window.removeEventListener("wheel", update);
      window.removeEventListener("touchmove", update);
    };
  }, [props.scrolled]);

  const contextTheme = useThemeMode();
  // Single source of truth: favor props from Header
  const effectiveScrolled = props.scrolled !== undefined ? props.scrolled : scrolled;
  const isOpaque = props.forceSolid || (props.isOpaque !== undefined ? props.isOpaque : effectiveScrolled);
  const isDarkMode = props.darkMode !== undefined ? props.darkMode : contextTheme.darkMode;
  const currentThemeMode = props.themeMode || contextTheme.themeMode || "system";
  const toggleDarkMode = props.toggleDarkMode || contextTheme.toggleDarkMode;

  const switchLanguage = (targetLang) => {
    try {
      if (currentActiveLang === targetLang) return;
      haptic.selection();

      const dateLocale = targetLang === "en" ? "en-gb" : "fr";
      i18n.changeLanguage(targetLang);
      moment.locale(dateLocale);
      localStorage.setItem("lang", JSON.stringify({ langLocale: targetLang, dateLocale }));

      const currentPath = window.location.pathname + window.location.search + window.location.hash;
      const target = toLocalizedPath(currentPath, targetLang);

      navigateWithIframeEscape(target, navigate);
    } catch (e) {
      console.error("[HeaderLinks] Language switch error:", e);
    }
  };

  useEffect(() => {
    setLoggedIn(!!(auth.profile && auth.profile.uid));
  }, [auth.profile]);

  const themeTooltipTitle = useMemo(() => {
    if (currentThemeMode === "dark") {
      return currentActiveLang === "fr"
        ? "Mode Sombre (🌙) — Cliquer pour Système (🖥️)"
        : "Dark Mode (🌙) — Click for System (🖥️)";
    } else if (currentThemeMode === "light") {
      return currentActiveLang === "fr"
        ? "Mode Clair (☀️) — Cliquer pour Sombre (🌙)"
        : "Light Mode (☀️) — Click for Dark (🌙)";
    } else {
      return currentActiveLang === "fr"
        ? "Mode Système (🖥️) — Cliquer pour Clair (☀️)"
        : "System Mode (🖥️) — Click for Light (☀️)";
    }
  }, [currentThemeMode, currentActiveLang]);

  const renderThemeIcon = (iconFontSize = 20) => {
    if (currentThemeMode === "dark") {
      // 🌙 Lune correspond à Sombre / Dark
      return <DarkModeIcon sx={{ fontSize: iconFontSize }} />;
    } else if (currentThemeMode === "light") {
      // ☀️ Soleil correspond à Clair / Light
      return <LightModeIcon sx={{ fontSize: iconFontSize }} />;
    } else {
      // 🖥️ Écran correspond à Système / System
      return <DesktopWindowsIcon sx={{ fontSize: iconFontSize }} />;
    }
  };

  // Reusable Theme Button with perfect horizontal alignment
  const themeButtonElement = (
    <Tooltip title={themeTooltipTitle} placement="bottom">
      <IconButton
        aria-label={themeTooltipTitle}
        onClick={toggleDarkMode}
        sx={{
          width: 36,
          height: 36,
          borderRadius: "10px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: isDarkMode || !isOpaque ? "#FFFFFF" : "#1e293b",
          bgcolor: isDarkMode || !isOpaque ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.05)",
          border: `1px solid ${isDarkMode || !isOpaque ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.12)"}`,
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          p: 0,
          m: 0,
          "&:hover": {
            bgcolor: isDarkMode || !isOpaque ? "rgba(255, 255, 255, 0.22)" : "rgba(0, 0, 0, 0.1)",
            transform: "scale(1.08)",
          },
        }}
      >
        {renderThemeIcon(20)}
      </IconButton>
    </Tooltip>
  );

  const inDrawer = props.section === "drawer" || props.isDrawer;

  // Reusable Language Toggle Capsule with perfect horizontal alignment
  const languageCapsuleElement = (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: 36,
        gap: 0.5,
        bgcolor: inDrawer
          ? (isDarkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.05)")
          : (isDarkMode || !isOpaque ? "rgba(0, 0, 0, 0.4)" : "rgba(0, 0, 0, 0.05)"),
        p: "2px",
        borderRadius: "24px",
        border: `1px solid ${inDrawer
          ? (isDarkMode ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.1)")
          : (isDarkMode || !isOpaque ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.12)")
          }`,
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        boxShadow: inDrawer
          ? "none"
          : (isDarkMode || !isOpaque ? "inset 0 1px 2px rgba(0, 0, 0, 0.4)" : "inset 0 1px 2px rgba(0, 0, 0, 0.04)"),
      }}
    >
      <Box
        onClick={() => switchLanguage("fr")}
        sx={{
          px: 1.3,
          height: "30px",
          borderRadius: "18px",
          cursor: "pointer",
          userSelect: "none",
          fontWeight: 900,
          bgcolor: currentActiveLang === "fr" ? "#E5A93C" : "transparent",
          color: currentActiveLang === "fr"
            ? "#000000 !important"
            : inDrawer
              ? (isDarkMode ? "#FFFFFF !important" : "#1E293B !important")
              : (isDarkMode || !isOpaque ? "#FFFFFF !important" : "#0F172A !important"),
          display: "inline-flex",
          alignItems: "center",
          gap: 0.6,
          boxShadow: currentActiveLang === "fr" ? "0 2px 6px rgba(0,0,0,0.25)" : "none",
          transform: currentActiveLang === "fr" ? "scale(1.02)" : "scale(1)",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            bgcolor: currentActiveLang === "fr"
              ? "#d4af37"
              : inDrawer
                ? (isDarkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.06)")
                : (isDarkMode || !isOpaque ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.06)"),
          },
        }}
      >
        <span style={{ fontSize: "1.05rem", lineHeight: 1, display: "flex", alignItems: "center" }}>🇫🇷</span>
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: "0.78rem",
            letterSpacing: "0.5px",
            lineHeight: 1,
            color: currentActiveLang === "fr"
              ? "#000000 !important"
              : inDrawer
                ? (isDarkMode ? "#FFFFFF !important" : "#1E293B !important")
                : (isDarkMode || !isOpaque ? "#FFFFFF !important" : "#0F172A !important"),
          }}
        >
          FR
        </Typography>
      </Box>

      <Box
        onClick={() => switchLanguage("en")}
        sx={{
          px: 1.3,
          height: "30px",
          borderRadius: "18px",
          cursor: "pointer",
          userSelect: "none",
          fontWeight: 900,
          bgcolor: currentActiveLang === "en" ? "#E5A93C" : "transparent",
          color: currentActiveLang === "en"
            ? "#000000 !important"
            : inDrawer
              ? (isDarkMode ? "#FFFFFF !important" : "#1E293B !important")
              : (isDarkMode || !isOpaque ? "#FFFFFF !important" : "#0F172A !important"),
          display: "inline-flex",
          alignItems: "center",
          gap: 0.6,
          boxShadow: currentActiveLang === "en" ? "0 2px 6px rgba(0,0,0,0.25)" : "none",
          transform: currentActiveLang === "en" ? "scale(1.02)" : "scale(1)",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            bgcolor: currentActiveLang === "en"
              ? "#d4af37"
              : inDrawer
                ? (isDarkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.06)")
                : (isDarkMode || !isOpaque ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.06)"),
          },
        }}
      >
        <span style={{ fontSize: "1.05rem", lineHeight: 1, display: "flex", alignItems: "center" }}>🇬🇧</span>
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: "0.78rem",
            letterSpacing: "0.5px",
            lineHeight: 1,
            color: currentActiveLang === "en"
              ? "#000000 !important"
              : inDrawer
                ? (isDarkMode ? "#FFFFFF !important" : "#1E293B !important")
                : (isDarkMode || !isOpaque ? "#FFFFFF !important" : "#0F172A !important"),
          }}
        >
          EN
        </Typography>
      </Box>
    </Box>
  );

  // Hardened nav styles (sx) — avoid dependence on JSS/emotion injection order in production builds
  const navColor = isDarkMode || !isOpaque ? "#ffffff" : "rgba(0, 0, 0, 0.85)";
  const NAV_FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  const navButtonSx = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: "36px",
    minHeight: "36px",
    py: 0,
    px: 1.5,
    gap: 0.7,
    lineHeight: 1,
    minWidth: "auto",
    borderRadius: "8px",
    textTransform: "uppercase",
    fontFamily: NAV_FONT,
    color: `${navColor} !important`,
    "& .MuiSvgIcon-root": { color: `${navColor} !important` },
    "&:hover, &:focus": {
      color: `${navColor} !important`,
      bgcolor: "rgba(200, 200, 200, 0.2)",
    },
  };
  const navTextSx = {
    fontFamily: `${NAV_FONT} !important`,
    fontSize: "12.5px !important",
    fontWeight: "600 !important",
    letterSpacing: "0.6px !important",
    textTransform: "uppercase !important",
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
    color: "inherit !important",
  };

  // Section 1: Navigation Menu (Centered in Header on Desktop)
  if (props.section === "nav") {
    return (
      <Box
        component="ul"
        sx={{
          display: "flex",
          alignItems: "center",
          listStyle: "none",
          margin: 0,
          padding: 0,
          gap: { md: 0.5, lg: 1.2 },
          height: "40px",
        }}
      >
        <Box component="li" sx={{ display: "inline-flex", alignItems: "center" }}>
          <Button
            color="transparent"
            className={classes.navLink}
            onClick={(e) => {
              e.preventDefault();
              localizedNavigate("/");
            }}
            sx={navButtonSx}
          >
            <House sx={{ fontSize: "20px !important", m: 0, color: "inherit" }} />
            <Typography sx={navTextSx}>
              {t("home")}
            </Typography>
          </Button>
        </Box>

        <Box component="li" sx={{ display: "inline-flex", alignItems: "center" }}>
          <Button
            color="transparent"
            className={classes.navLink}
            onClick={(e) => {
              e.preventDefault();
              if (loggedIn) {
                localizedNavigate("/bookings");
              } else {
                localizedNavigate("/login");
              }
            }}
            sx={navButtonSx}
          >
            <AccountBox sx={{ fontSize: "20px !important", m: 0, color: "inherit" }} />
            <Typography sx={navTextSx}>
              {loggedIn ? t("myaccount") : t("login_signup")}
            </Typography>
          </Button>
        </Box>

        <Box component="li" sx={{ display: "inline-flex", alignItems: "center" }}>
          <Button
            color="transparent"
            className={classes.navLink}
            onClick={(e) => {
              e.preventDefault();
              localizedNavigate("/about-us");
            }}
            sx={navButtonSx}
          >
            <Info sx={{ fontSize: "20px !important", m: 0, color: "inherit" }} />
            <Typography sx={navTextSx}>
              {t("about_us")}
            </Typography>
          </Button>
        </Box>

        <Box component="li" sx={{ display: "inline-flex", alignItems: "center" }}>
          <Button
            color="transparent"
            className={classes.navLink}
            onClick={(e) => {
              e.preventDefault();
              localizedNavigate("/contact-us");
            }}
            sx={navButtonSx}
          >
            <EmailIcon sx={{ fontSize: "20px !important", m: 0, color: "inherit" }} />
            <Typography sx={navTextSx}>
              {t("contact_us")}
            </Typography>
          </Button>
        </Box>

        {settings && settings.InstagramHandle ? (
          <Box component="li" sx={{ display: "inline-flex", alignItems: "center" }}>
            <Tooltip
              id="instagram-twitter"
              title={t("follow_instagram")}
              placement="top"
              classes={{ tooltip: classes.tooltip }}
            >
              <Button
                href={settings.InstagramHandle}
                target="_blank"
                color="transparent"
                className={classes.navLink}
                sx={{ ...navButtonSx, px: 1 }}
              >
                <i className={classes.socialIcons + " fab fa-instagram"} />
              </Button>
            </Tooltip>
          </Box>
        ) : null}
      </Box>
    );
  }

  // Section 1b: Mobile AppBar language capsule (between logo and hamburger)
  if (props.section === "mobile-lang") {
    return languageCapsuleElement;
  }

  // Section 2: Desktop Actions (Theme Mode Toggle + Language Capsule aligned horizontally)
  if (props.section === "actions") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          height: "40px",
        }}
      >
        {themeButtonElement}
        {languageCapsuleElement}
      </Box>
    );
  }

  // Section 3: Drawer Menu for Mobile
  if (props.section === "drawer" || props.isDrawer) {
    const handleDrawerNavigate = (path) => {
      if (props.onItemClick) {
        props.onItemClick();
      }
      localizedNavigate(path);
    };

    return (
      <List disablePadding sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
        {/* 1. Accueil */}
        <ListItem disablePadding sx={{ width: "100%", mb: 0.5 }}>
          <ListItemButton
            onClick={(e) => {
              e.preventDefault();
              handleDrawerNavigate("/");
            }}
            sx={{
              py: 1.2,
              px: 2,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              gap: 1.8,
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#d4af37",
                flexShrink: 0,
              }}
            >
              <House sx={{ fontSize: 24 }} />
            </Box>
            <Typography
              fontFamily={FONT_FAMILY}
              sx={{
                fontWeight: 600,
                fontSize: "15px",
                color: "text.primary",
                lineHeight: 1.2,
              }}
            >
              {t("home")}
            </Typography>
          </ListItemButton>
        </ListItem>

        {/* 2. Mon Compte / Connexion */}
        <ListItem disablePadding sx={{ width: "100%", mb: 0.5 }}>
          <ListItemButton
            onClick={(e) => {
              e.preventDefault();
              if (loggedIn) {
                handleDrawerNavigate("/bookings");
              } else {
                handleDrawerNavigate("/login");
              }
            }}
            sx={{
              py: 1.2,
              px: 2,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              gap: 1.8,
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#d4af37",
                flexShrink: 0,
              }}
            >
              <AccountBox sx={{ fontSize: 24 }} />
            </Box>
            <Typography
              fontFamily={FONT_FAMILY}
              sx={{
                fontWeight: 600,
                fontSize: "15px",
                color: "text.primary",
                lineHeight: 1.2,
              }}
            >
              {loggedIn ? t("myaccount") : t("login_signup")}
            </Typography>
          </ListItemButton>
        </ListItem>

        {/* 3. À propos de nous */}
        <ListItem disablePadding sx={{ width: "100%", mb: 0.5 }}>
          <ListItemButton
            onClick={(e) => {
              e.preventDefault();
              handleDrawerNavigate("/about-us");
            }}
            sx={{
              py: 1.2,
              px: 2,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              gap: 1.8,
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#d4af37",
                flexShrink: 0,
              }}
            >
              <Info sx={{ fontSize: 24 }} />
            </Box>
            <Typography
              fontFamily={FONT_FAMILY}
              sx={{
                fontWeight: 600,
                fontSize: "15px",
                color: "text.primary",
                lineHeight: 1.2,
              }}
            >
              {t("about_us")}
            </Typography>
          </ListItemButton>
        </ListItem>

        {/* 4. Contactez-nous */}
        <ListItem disablePadding sx={{ width: "100%", mb: 0.5 }}>
          <ListItemButton
            onClick={(e) => {
              e.preventDefault();
              handleDrawerNavigate("/contact-us");
            }}
            sx={{
              py: 1.2,
              px: 2,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              gap: 1.8,
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#d4af37",
                flexShrink: 0,
              }}
            >
              <EmailIcon sx={{ fontSize: 24 }} />
            </Box>
            <Typography
              fontFamily={FONT_FAMILY}
              sx={{
                fontWeight: 600,
                fontSize: "15px",
                color: "text.primary",
                lineHeight: 1.2,
              }}
            >
              {t("contact_us")}
            </Typography>
          </ListItemButton>
        </ListItem>

        {/* Instagram si disponible */}
        {settings && settings.InstagramHandle ? (
          <ListItem disablePadding sx={{ width: "100%", mb: 0.5 }}>
            <ListItemButton
              component="a"
              href={settings.InstagramHandle}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                py: 1.2,
                px: 2,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                gap: 1.8,
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#d4af37",
                  flexShrink: 0,
                }}
              >
                <i className="fab fa-instagram" style={{ fontSize: 22 }} />
              </Box>
              <Typography
                fontFamily={FONT_FAMILY}
                sx={{
                  fontWeight: 600,
                  fontSize: "15px",
                  color: "text.primary",
                  lineHeight: 1.2,
                }}
              >
                Instagram
              </Typography>
            </ListItemButton>
          </ListItem>
        ) : null}

        {/* Divider épuré entre navigation et préférences */}
        <Divider
          sx={{
            my: 1.5,
            mx: 1.5,
            borderColor: (theme) =>
              theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)",
          }}
        />

        {/* 5. Mode Thème */}
        <ListItem disablePadding sx={{ width: "100%", mb: 0.5 }}>
          <ListItemButton
            onClick={toggleDarkMode}
            sx={{
              py: 1.2,
              px: 2,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.8 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#d4af37",
                  flexShrink: 0,
                }}
              >
                {renderThemeIcon(22)}
              </Box>
              <Typography
                fontFamily={FONT_FAMILY}
                sx={{
                  fontWeight: 600,
                  fontSize: "15px",
                  color: "text.primary",
                  lineHeight: 1.2,
                }}
              >
                {currentActiveLang === "fr" ? "Mode Thème" : "Theme Mode"}
              </Typography>
            </Box>

            <Box
              sx={{
                px: 1.2,
                py: 0.4,
                borderRadius: "8px",
                fontSize: "12px",
                fontWeight: 700,
                fontFamily: FONT_FAMILY,
                display: "inline-flex",
                alignItems: "center",
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.06)",
                color: "text.secondary",
              }}
            >
              {currentThemeMode === "dark"
                ? (currentActiveLang === "fr" ? "Sombre 🌙" : "Dark 🌙")
                : currentThemeMode === "light"
                  ? (currentActiveLang === "fr" ? "Clair ☀️" : "Light ☀️")
                  : (currentActiveLang === "fr" ? "Système 🖥️" : "System 🖥️")}
            </Box>
          </ListItemButton>
        </ListItem>

      </List>
    );
  }

  // Fallback: Default full render (when section is unspecified)
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, height: "40px" }}>
      <Box
        component="ul"
        sx={{
          display: "flex",
          alignItems: "center",
          listStyle: "none",
          margin: 0,
          padding: 0,
          gap: 1,
        }}
      >
        <Box component="li" sx={{ display: "inline-flex", alignItems: "center" }}>
          <Button
            color="transparent"
            className={classes.navLink}
            onClick={(e) => {
              e.preventDefault();
              localizedNavigate("/");
            }}
            sx={{ height: "36px", minHeight: "36px", py: 0, px: 1.5, display: "inline-flex", alignItems: "center" }}
          >
            <House sx={{ fontSize: "20px !important", m: 0 }} />
            <Typography fontFamily={FONT_FAMILY} sx={{ fontSize: "12px", fontWeight: 600, ml: 0.7 }}>
              {t("home")}
            </Typography>
          </Button>
        </Box>

        <Box component="li" sx={{ display: "inline-flex", alignItems: "center" }}>
          <Button
            color="transparent"
            className={classes.navLink}
            onClick={(e) => {
              e.preventDefault();
              if (loggedIn) localizedNavigate("/bookings");
              else localizedNavigate("/login");
            }}
            sx={{ height: "36px", minHeight: "36px", py: 0, px: 1.5, display: "inline-flex", alignItems: "center" }}
          >
            <AccountBox sx={{ fontSize: "20px !important", m: 0 }} />
            <Typography fontFamily={FONT_FAMILY} sx={{ fontSize: "12px", fontWeight: 600, ml: 0.7 }}>
              {loggedIn ? t("myaccount") : t("login_signup")}
            </Typography>
          </Button>
        </Box>

        <Box component="li" sx={{ display: "inline-flex", alignItems: "center" }}>
          <Button
            color="transparent"
            className={classes.navLink}
            onClick={(e) => {
              e.preventDefault();
              localizedNavigate("/about-us");
            }}
            sx={{ height: "36px", minHeight: "36px", py: 0, px: 1.5, display: "inline-flex", alignItems: "center" }}
          >
            <Info sx={{ fontSize: "20px !important", m: 0 }} />
            <Typography fontFamily={FONT_FAMILY} sx={{ fontSize: "12px", fontWeight: 600, ml: 0.7 }}>
              {t("about_us")}
            </Typography>
          </Button>
        </Box>

        <Box component="li" sx={{ display: "inline-flex", alignItems: "center" }}>
          <Button
            color="transparent"
            className={classes.navLink}
            onClick={(e) => {
              e.preventDefault();
              localizedNavigate("/contact-us");
            }}
            sx={{ height: "36px", minHeight: "36px", py: 0, px: 1.5, display: "inline-flex", alignItems: "center" }}
          >
            <EmailIcon sx={{ fontSize: "20px !important", m: 0 }} />
            <Typography fontFamily={FONT_FAMILY} sx={{ fontSize: "12px", fontWeight: 600, ml: 0.7 }}>
              {t("contact_us")}
            </Typography>
          </Button>
        </Box>
      </Box>

      {themeButtonElement}
      {!isMobile && languageCapsuleElement}
    </Box>
  );
}
